import { BILLING_OPTIONS, type Activity, type LeadStatus } from '@delvero/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { fmtDate, fmtDur, fmtMin, fmtPayback, fmtZl } from '../lib/format';
import { orpc } from '../lib/orpc';
import { useShell } from '../shell';
import { Icon, type IconName } from './Icon';
import { StatusSelect } from './Status';

const ACT_META: Record<Activity['type'], { icon: IconName; label: string }> = {
  call: { icon: 'phone', label: 'Call' },
  email: { icon: 'mailIc', label: 'Mail' },
  note: { icon: 'noteIc', label: 'Notatka' },
  status: { icon: 'flag', label: 'Status' },
};

export function LeadDrawer({ leadId, onClose }: { leadId: string; onClose: () => void }) {
  const { activeApp } = useShell();
  const queryClient = useQueryClient();
  const [noteText, setNoteText] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const leadQuery = useQuery(orpc.leads.get.queryOptions({ input: { id: leadId } }));
  const settingsQuery = useQuery(orpc.settings.get.queryOptions());

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: orpc.leads.key() });
    void queryClient.invalidateQueries({ queryKey: orpc.dashboard.key() });
    void queryClient.invalidateQueries({ queryKey: orpc.costs.key() });
  };

  const updateLead = useMutation(orpc.leads.update.mutationOptions({ onSuccess: invalidate }));
  const addActivity = useMutation(
    orpc.leads.addActivity.mutationOptions({
      onSuccess: () => {
        setNoteText('');
        invalidate();
      },
    }),
  );

  const lead = leadQuery.data;

  return (
    <div className="scrim" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        {!lead ? (
          <div className="center-pad">
            <span className="spinner" /> Wczytywanie…
          </div>
        ) : (
          (() => {
            const e = lead.econ;
            const rate = settingsQuery.data?.hourlyRate ?? 0;
            const emailMin = settingsQuery.data?.emailMinutes ?? 0;
            const bil = BILLING_OPTIONS.find((b) => b.id === lead.billing) ?? BILLING_OPTIONS[0];
            const acts = [...lead.activities].reverse();
            return (
              <>
                <div className="drawer-head">
                  <div>
                    <div className="drawer-company">{lead.company}</div>
                    <div className="cell-sub">
                      {lead.contact}
                      {lead.role ? ` · ${lead.role}` : ''}
                    </div>
                  </div>
                  <button className="icon-btn" onClick={onClose} aria-label="Zamknij">
                    <Icon name="x" size={15} />
                  </button>
                </div>

                <div className="drawer-meta">
                  <div className="meta-item">
                    <span className="meta-l">Telefon</span>
                    <span className="mono">{lead.phone || '—'}</span>
                  </div>
                  <div className="meta-item">
                    <span className="meta-l">E-mail</span>
                    <span>{lead.email || '—'}</span>
                  </div>
                  <div className="meta-item">
                    <span className="meta-l">Źródło</span>
                    <span>{lead.campaignName ?? '—'}</span>
                  </div>
                  <div className="meta-item">
                    <span className="meta-l">Dodany</span>
                    <span>{fmtDate(lead.createdAt)}</span>
                  </div>
                </div>

                <div className="drawer-controls">
                  <StatusSelect
                    value={lead.status}
                    onChange={(v: LeadStatus) => updateLead.mutate({ id: lead.id, patch: { status: v } })}
                  />
                  <select
                    className="input slim"
                    value={lead.planId ?? ''}
                    onChange={(ev) => updateLead.mutate({ id: lead.id, patch: { planId: ev.target.value || null } })}
                  >
                    {(activeApp?.plans ?? []).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} — {fmtZl(p.price)}/mc
                      </option>
                    ))}
                  </select>
                  <select
                    className="input slim"
                    value={lead.billing}
                    onChange={(ev) =>
                      updateLead.mutate({ id: lead.id, patch: { billing: ev.target.value as typeof lead.billing } })
                    }
                  >
                    {BILLING_OPTIONS.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.label}
                        {b.hint ? ` (${b.hint})` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="econ-card">
                  <div className="econ-title">Ekonomia leada</div>
                  <div className="econ-line">
                    <span>
                      Calle — {e.callCount} szt., {fmtMin(e.callSec)}
                    </span>
                    <span className="mono">{fmtZl((e.callSec / 3600) * rate)}</span>
                  </div>
                  <div className="econ-line">
                    <span>
                      Maile — {e.emails} × {emailMin} min
                    </span>
                    <span className="mono">{fmtZl(((e.emails * emailMin) / 60) * rate)}</span>
                  </div>
                  <div className="econ-line">
                    <span>Reklama ({lead.campaignName ?? '—'})</span>
                    <span className="mono">{fmtZl(e.adCost)}</span>
                  </div>
                  <div className="econ-line total">
                    <span>Koszt pozyskania (CAC)</span>
                    <span className="mono">{fmtZl(e.cac)}</span>
                  </div>
                  {lead.status === 'sub' && lead.planName ? (
                    <div className="econ-sub">
                      <div className="econ-line">
                        <span>
                          MRR ({lead.planName}, {bil.label.toLowerCase()}
                          {bil.hint ? `, ${bil.hint}` : ''})
                        </span>
                        <span className="mono acc">{fmtZl(e.mrr)}/mc</span>
                      </div>
                      <div className="econ-line">
                        <span>
                          Aktywny od {lead.subStart ? fmtDate(lead.subStart) : '—'} — {e.months} mc
                        </span>
                        <span className="mono">{fmtZl(e.revenue)}</span>
                      </div>
                      <div className="econ-line total">
                        <span>Zwrot (przychód − CAC)</span>
                        <span className={`mono ${(e.ret ?? 0) >= 0 ? 'acc' : 'neg'}`}>
                          {(e.ret ?? 0) >= 0 ? '+' : ''}
                          {fmtZl(e.ret ?? 0)}
                        </span>
                      </div>
                      <div className="econ-line">
                        <span>Payback</span>
                        <span className="mono">{fmtPayback(e.payback)}</span>
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className="composer">
                  <textarea
                    className="input"
                    rows={2}
                    placeholder="Dodaj notatkę lub opis maila…"
                    value={noteText}
                    onChange={(ev) => setNoteText(ev.target.value)}
                  />
                  <div className="composer-btns">
                    <button
                      className="btn"
                      disabled={!noteText.trim() || addActivity.isPending}
                      onClick={() => addActivity.mutate({ id: lead.id, type: 'note', note: noteText.trim() })}
                    >
                      <Icon name="noteIc" size={13} /> Notatka
                    </button>
                    <button
                      className="btn"
                      disabled={addActivity.isPending}
                      onClick={() => addActivity.mutate({ id: lead.id, type: 'email', note: noteText.trim() })}
                    >
                      <Icon name="mailIc" size={13} /> Zaloguj cold mail
                    </button>
                  </div>
                </div>

                <div className="timeline">
                  <div className="econ-title">Historia ({acts.length})</div>
                  {acts.map((a) => {
                    const m = ACT_META[a.type] ?? ACT_META.note;
                    return (
                      <div className="tl-item" key={a.id}>
                        <span className="tl-icon">
                          <Icon name={m.icon} size={12} />
                        </span>
                        <div className="tl-body">
                          <div className="tl-head">
                            <b>{m.label}</b>
                            {a.type === 'call' && a.durationSec != null ? (
                              <span className="mono tl-dur">{fmtDur(a.durationSec)}</span>
                            ) : null}
                            {a.outcome ? <span className="tl-outcome">{a.outcome}</span> : null}
                            <span className="tl-date">{fmtDate(a.at)}</span>
                          </div>
                          {a.note ? <div className="tl-note">{a.note}</div> : null}
                        </div>
                      </div>
                    );
                  })}
                  {acts.length === 0 ? (
                    <div className="empty-cell">Brak aktywności — zacznij od cold maila albo calla.</div>
                  ) : null}
                </div>
              </>
            );
          })()
        )}
      </div>
    </div>
  );
}
